/**
 * useStreamingChat Hook
 * Manages streaming chat with chain-of-thought UI support
 */

import { useState, useCallback, useRef } from "react";
import { llmChatStreamService } from "../services/api/llm-chat-stream";
import { StreamMessageRequest } from "../types/chat-stream";

export interface StreamingChatState {
  isStreaming: boolean;
  thinkingMessage: string;
  currentAnswer: string;
  messageId: string | null;
  error: string | null;
}

export interface UseStreamingChatOptions {
  onStreamStart?: () => void;
  onThinkingChange?: (message: string) => void;
  onAnswerChunk?: (chunk: string, fullText: string, messageId?: string) => void;
  onStreamEnd?: (messageId: string | undefined, finalAnswer: string) => void;
  onError?: (error: string, errorType?: string) => void;
}

export function useStreamingChat(options: UseStreamingChatOptions = {}) {
  const [state, setState] = useState<StreamingChatState>({
    isStreaming: false,
    thinkingMessage: "Thinking",
    currentAnswer: "",
    messageId: null,
    error: null,
  });

  const abortRef = useRef<(() => void) | null>(null);
  // Track accumulated answer to avoid stale closure in onStreamEnd
  const accumulatedAnswerRef = useRef<string>("");

  /**
   * Start streaming chat message
   */
  const startStream = useCallback(
    async (request: StreamMessageRequest) => {
      // Reset state and ref
      accumulatedAnswerRef.current = "";
      setState({
        isStreaming: true,
        thinkingMessage: "Thinking",
        currentAnswer: "",
        messageId: null,
        error: null,
      });

      options.onStreamStart?.();

      const abort = await llmChatStreamService.sendMessageStream(request, {
        onThought: (message) => {
          setState((prev) => ({
            ...prev,
            thinkingMessage: message,
          }));
          // Notify parent component of thinking status change
          options.onThinkingChange?.(message);
        },
        onAnswer: (_chunk, fullText, messageId) => {
          // Track in ref for onStreamEnd callback
          accumulatedAnswerRef.current = fullText;
          setState((prev) => ({
            ...prev,
            currentAnswer: fullText,
            messageId: messageId || prev.messageId,
            thinkingMessage: "", // Clear thinking when answer starts
          }));
          // Notify parent component of answer chunk - THIS IS THE KEY!
          // Parent can update messages array directly like extension does
          options.onAnswerChunk?.(_chunk, fullText, messageId);
        },
        onComplete: (messageId) => {
          const finalAnswer = accumulatedAnswerRef.current;
          // Call onStreamEnd first to add message before UI hides
          options.onStreamEnd?.(messageId, finalAnswer);
          // Then set isStreaming to false to hide the streaming UI
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            messageId: messageId || prev.messageId,
          }));
        },
        onError: (error, errorType) => {
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error,
          }));
          options.onError?.(error, errorType);
        },
      });

      abortRef.current = abort;
    },
    [options],
  );

  /**
   * Stop the current stream
   */
  const stopStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
      setState((prev) => ({
        ...prev,
        isStreaming: false,
      }));
    }
  }, []);

  /**
   * Reset state for new message
   */
  const reset = useCallback(() => {
    stopStream();
    setState({
      isStreaming: false,
      thinkingMessage: "Thinking",
      currentAnswer: "",
      messageId: null,
      error: null,
    });
  }, [stopStream]);

  return {
    ...state,
    startStream,
    stopStream,
    reset,
  };
}
